/** EmailThreadModal — Gmail-threaded conversation view for mass-email sends.
 * Shows outbound + inbound messages on one thread and lets you reply
 * (free-form or template-based) straight from the portal via Gmail. */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Send, Loader2, Reply, FileText, PenLine, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function EmailThreadModal({ threadId, onClose, onSent }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("write"); // write | template
  const [body, setBody] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get(`/email/threads/${threadId}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threadId]);
  useEffect(() => {
    api.get("/email/templates").then(({ data: d }) => setTemplates(d || [])).catch(() => {});
    api.get("/email/accounts").then(({ data: d }) => {
      setAccounts(d || []);
      const def = (d || []).find((a) => a.default || a.is_default) || (d || [])[0];
      if (def) setAccountId(def.id);
    }).catch(() => {});
  }, []);

  const tpl = templates.find((t) => t.id === templateId);

  const sendReply = async () => {
    if (mode === "write" && !body.trim()) { toast.error("Write a reply first"); return; }
    if (mode === "template" && !templateId) { toast.error("Pick a template first"); return; }
    setSending(true);
    try {
      const payload = { from_token_id: accountId || "" };
      if (mode === "template") payload.template_id = templateId;
      else payload.body_html = body.split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
      await api.post(`/email/threads/${threadId}/reply`, payload);
      toast.success("Reply sent on the same thread", { description: "It will also appear in your Gmail Sent folder." });
      setBody(""); setTemplateId("");
      await load();
      onSent && onSent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reply failed"); }
    setSending(false);
  };

  const msgs = data?.messages || [];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
      className="fixed inset-0 z-[85] flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm" data-testid="email-thread-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="flex h-[96vh] w-[96vw] max-w-[1500px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-100 bg-[#0F2042] px-5 py-3 text-white">
          <div className="min-w-0">
            <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/60">// gmail conversation</div>
            <div className="truncate font-display text-sm font-bold">{msgs[0]?.subject || "Email thread"}</div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={load} className="rounded-lg p-1.5 hover:bg-white/10" title="Refresh thread" data-testid="thread-refresh"><RefreshCw size={14} /></button>
            <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-white/10" data-testid="thread-close"><X size={16} /></button>
          </div>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4" data-testid="thread-messages">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 size={22} className="animate-spin text-slate-300" /></div>
          ) : msgs.length === 0 ? (
            <p className="py-10 text-center text-xs text-slate-400">No messages found on this thread yet.</p>
          ) : msgs.map((m, i) => (
            <div key={i} className={`max-w-[88%] ${m.direction === "out" ? "ml-auto" : ""}`} data-testid={`thread-msg-${i}`}>
              <div className={`rounded-2xl border p-3 shadow-sm ${m.direction === "out" ? "border-orange-200 bg-orange-50/70" : "border-slate-200 bg-white"}`}>
                <div className="mb-1.5 flex flex-wrap items-center gap-2 text-[10px]">
                  <span className={`rounded-full px-2 py-0.5 font-bold uppercase ${m.direction === "out" ? "bg-[#F97316] text-white" : "bg-[#0F2042] text-white"}`}>
                    {m.direction === "out" ? "You sent" : "Reply received"}
                  </span>
                  <span className="truncate font-semibold text-slate-600">{m.direction === "out" ? `to ${m.to}` : m.from}</span>
                  <span className="ml-auto text-slate-400">{m.at ? new Date(m.at).toLocaleString() : ""}</span>
                </div>
                {m.body_html ? (
                  <iframe title={`msg-${i}`} srcDoc={m.body_html} sandbox="" className="h-[26rem] w-full rounded-lg border border-slate-100 bg-white" />
                ) : (
                  <p className="whitespace-pre-wrap text-xs text-slate-700">{m.body_text || m.snippet || "(no content)"}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-200 bg-white p-4">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-500"><Reply size={11} /> Reply on this thread</span>
            <div className="flex rounded-full bg-slate-100 p-0.5 text-[10px] font-bold">
              <button onClick={() => setMode("write")} data-testid="thread-mode-write"
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 ${mode === "write" ? "bg-[#0F2042] text-white" : "text-slate-500"}`}><PenLine size={10} /> Write</button>
              <button onClick={() => setMode("template")} data-testid="thread-mode-template"
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 ${mode === "template" ? "bg-[#0F2042] text-white" : "text-slate-500"}`}><FileText size={10} /> Use template</button>
            </div>
            {accounts.length > 0 && (
              <select value={accountId} onChange={(e) => setAccountId(e.target.value)} data-testid="thread-from-account"
                className="ml-auto rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-600 outline-none">
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.email}</option>)}
              </select>
            )}
          </div>
          {mode === "write" ? (
            <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} data-testid="thread-reply-body"
              placeholder="Type your reply — it goes out on the same Gmail thread and shows in your Gmail Sent box…"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs leading-relaxed outline-none focus:border-[#F97316]" />
          ) : (
            <div className="space-y-2">
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="thread-reply-template"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-[#F97316]">
                <option value="">— Pick an email template —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.category ? ` · ${t.category}` : ""}</option>)}
              </select>
              {tpl && <iframe title="tpl-preview" srcDoc={tpl.body_html} sandbox="" className="h-[34vh] w-full rounded-xl border border-slate-200 bg-white" data-testid="thread-template-preview" />}
            </div>
          )}
          <div className="mt-2 flex items-center justify-between">
            <p className="text-[9px] text-slate-400">Sent via your connected Gmail — recipient sees it in the same conversation.</p>
            <button onClick={sendReply} disabled={sending} data-testid="thread-reply-send"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#F97316] px-5 py-2 text-xs font-bold text-white hover:bg-orange-600 disabled:opacity-50">
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send reply
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
